/**
 * The PO line's RECEIPT vocabulary. ONE CONSUMER TODAY, and the docstring says
 * so because the first version of it did not.
 *
 * It read "one owner, both grains of consumer", which was the intention and not
 * the fact: the only importer is the supplier-performance drill-down
 * (`pages/supplier-performance/drilldown/poLineColumns.tsx`), which is the
 * surface that had no table at all and printed the RAW database string. The
 * other three tables — `PODetailHelpers.getReceiptStatusBadge`,
 * `PrintablePOSummary`, and the dashboard PO-line drill-down — were relabelled
 * IN PLACE in this PR and still hold their own maps, so they still disagree
 * about `partially_received` ("Partial" here, "Partially Received" there).
 *
 * And one of those three renders NOWHERE: `getReceiptStatusBadge` has no
 * consumer at this head or at the merge base — `git grep` finds its definition
 * and nothing else. Relabelling it changed no screen. Said here because the
 * first version of this comment counted it as a surface, and a dead export
 * that looks maintained is how a reader concludes a vocabulary is covered when
 * it is not.
 *
 * Pointing them at this table is a one-word change to what two screens RENDER,
 * which is a sign-off question rather than a correctness one, so it is filed
 * (R-542) rather than taken here. A false claim in a docstring is the part that
 * had to go: the next person to relabel would have trusted it and moved one
 * table while three others drifted.
 *
 * The words themselves are Aaron's ruling of 18 Sep 2026 and they are legacy's:
 * `NetworkSQL.cs:6886` renders the same underlying bit as `case when
 * POL.Completed = 0 THEN 'Open' ELSE 'Closed'`, and a sweep of the legacy code
 * for `THEN 'Received'` returns nothing — Received is a QUANTITY word there,
 * never a state. So a finished line reads **Closed**.
 *
 * What deliberately keeps the word Received, because it means the quantity and
 * not the state: Qty Received, Qty Delivered, Last Received Date, and every GRN
 * and ASN label. This is not a find-and-replace.
 *
 * `partially_received` keeps its own wording: a part-received line is not
 * closed, and it is the one value where the quantity reading is the right one.
 */

/** The `status` values that mean the line is finished. Mirrors the server's
 *  `purchasing.closure_sources.CLOSED_LINE_STATUSES`, and
 *  `lineStatus.PO_LINE_CLOSED_RECEIPT_STATUSES` is the same list for the badge
 *  path; `tests/purchasing/test_closed_status_contract.py` pins them together. */
export const CLOSED_RECEIPT_STATUSES: readonly string[] = ['received', 'cancelled'];

/** What each receipt status is called on screen. */
export const RECEIPT_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  partially_received: 'Partially Received',
  received: 'Closed',
  cancelled: 'Cancelled',
};
